/**
 * Helpers for resolving stop ids to keys used in the stop-lines mapping.
 */

import { getGtfsStopIdSuffix, normalizeGtfsParentStationId } from "../tools/gtfsStopId.tools";

/** Options for resolving a prefixed stop id to a stop-lines mapping key. */
export interface StopLinesLookupOptions {

    /** GTFS source prefix when available (e.g. `tpg`, `ch_sbb`). */
    "gtfsSource"?: string | null;

    /** Parent station id from a child stop DTO when available. */
    "parentStation"?: string | null;
}

/**
 * Derives the SNCF-style mapping key by taking the suffix after the last `-`.
 *
 * @param stopIdSuffix Unprefixed GTFS stop id suffix.
 * @returns Mapping key when a `-` suffix is present, otherwise `null`.
 */
const resolveSncfMappingKey = ( stopIdSuffix: string ): string | null => {
    const dashIndex = stopIdSuffix.lastIndexOf( "-" );

    if ( dashIndex < 0 || dashIndex === stopIdSuffix.length - 1 ) {
        return null;
    }

    return stopIdSuffix.slice( dashIndex + 1 );
};

/**
 * Normalizes a GTFS stop id suffix to the parent stop key used by `/api/stops/lines`.
 *
 * @param stopIdSuffix Unprefixed GTFS stop id suffix.
 * @returns Mapping key (UIC numeric id or SNCF suffix).
 */
const normalizeStopIdSuffixToMappingKey = ( stopIdSuffix: string ): string => {
    const sncfKey = resolveSncfMappingKey( stopIdSuffix );

    if ( sncfKey !== null ) {
        return sncfKey;
    }

    const normalized = normalizeGtfsParentStationId( stopIdSuffix );
    const colonIndex = normalized.indexOf( ":" );

    if ( colonIndex >= 0 ) {
        return normalized.slice( 0, colonIndex );
    }

    return normalized;
};

/**
 * Resolves a prefixed stop id to the key used in `StopLinesMappingResponseDto.lines`.
 *
 * @param stopId Prefixed stop id from the spatial index or geocoder.
 * @param options Optional GTFS source and parent station hints.
 * @returns Mapping key, or an empty string when `stopId` is blank.
 */
export const resolveStopLinesMappingKey = (
    stopId: string,
    options: StopLinesLookupOptions = {},
): string => {
    const trimmedStopId = stopId.trim();

    if ( trimmedStopId === "" ) {
        return "";
    }

    const parentStation = options.parentStation?.trim() ?? "";

    if ( parentStation !== "" ) {
        const parentGtfsSource = options.gtfsSource ?? (
            parentStation.includes( "_" ) ? parentStation.split( "_" )[0] ?? null : null
        );
        const parentSuffix = getGtfsStopIdSuffix( parentStation, parentGtfsSource );

        return normalizeStopIdSuffixToMappingKey( parentSuffix );
    }

    const gtfsSource = options.gtfsSource ?? (
        trimmedStopId.includes( "_" ) ? trimmedStopId.split( "_" )[0] ?? null : null
    );
    const stopIdSuffix = getGtfsStopIdSuffix( trimmedStopId, gtfsSource );

    return normalizeStopIdSuffixToMappingKey( stopIdSuffix );
};
